import { useEffect, useRef, useState } from "react";

export function useOneShotTime(durationSec: number, onComplete?: () => void): number {
  const [t, setT] = useState(0);
  const cbRef = useRef(onComplete);
  const completedRef = useRef(false);

  useEffect(() => {
    cbRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    completedRef.current = false;
    setT(0);
    const start = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = (now - start) / 1000;
      const clamped = Math.min(elapsed, durationSec);
      setT(clamped);
      if (elapsed >= durationSec) {
        if (!completedRef.current) {
          completedRef.current = true;
          cbRef.current?.();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationSec]);

  return t;
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

// Continuous monotonic clock for ambient effects (idle vault dial spin, keyhole
// pulses). Auto-pauses on hidden tabs via RAF. Reduced-motion freezes at 0.
export function useTime(): number {
  const reduced = useReducedMotion();
  const [t, setT] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      setT((now - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  return t;
}
